import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  DivineServiceState,
  INITIAL_DIVINE_SERVICE_STATE,
  OptionalFieldsVisibility,
  INITIAL_OPTIONAL_FIELDS_VISIBILITY
} from '../../models/divine-service-state.model';

/**
 * Servicio centralizado para gestionar el estado de un Servicio Divino
 * Proporciona un único punto de verdad para todos los datos del servicio
 * y la visibilidad de campos opcionales
 */
@Injectable({
  providedIn: 'root'
})
export class DivineServiceStateService {
  private state$ = new BehaviorSubject<DivineServiceState>(INITIAL_DIVINE_SERVICE_STATE);
  private optionalFieldsVisibility$ = new BehaviorSubject<OptionalFieldsVisibility>(
    INITIAL_OPTIONAL_FIELDS_VISIBILITY
  );

  constructor() {}

  /**
   * Observable del estado completo del servicio divino
   */
  getState$(): Observable<DivineServiceState> {
    return this.state$.asObservable();
  }

  /**
   * Observable de la visibilidad de campos opcionales
   */
  getOptionalFieldsVisibility$(): Observable<OptionalFieldsVisibility> {
    return this.optionalFieldsVisibility$.asObservable();
  }

  /**
   * Obtiene el estado actual (snapshot)
   */
  getState(): DivineServiceState {
    return this.state$.value;
  }

  /**
   * Obtiene la visibilidad actual de campos opcionales
   */
  getOptionalFieldsVisibility(): OptionalFieldsVisibility {
    return this.optionalFieldsVisibility$.value;
  }

  /**
   * Actualiza una o más propiedades del estado
   */
  updateState(updates: Partial<DivineServiceState>): void {
    const currentState = this.state$.value;
    this.state$.next({ ...currentState, ...updates });
  }

  /**
   * Actualiza la visibilidad de un campo opcional específico
   */
  updateFieldVisibility(field: keyof OptionalFieldsVisibility, visible: boolean): void {
    const currentVisibility = this.optionalFieldsVisibility$.value;
    this.optionalFieldsVisibility$.next({
      ...currentVisibility,
      [field]: visible
    });
  }

  /**
   * Establece la visibilidad de múltiples campos opcionales
   */
  updateFieldsVisibility(updates: Partial<OptionalFieldsVisibility>): void {
    const currentVisibility = this.optionalFieldsVisibility$.value;
    this.optionalFieldsVisibility$.next({ ...currentVisibility, ...updates });
  }

  /**
   * Resetea el estado a sus valores iniciales
   */
  resetState(): void {
    this.state$.next({ ...INITIAL_DIVINE_SERVICE_STATE });
    this.optionalFieldsVisibility$.next({ ...INITIAL_OPTIONAL_FIELDS_VISIBILITY });
  }

  /**
   * Inicializa el estado con un servicio existente
   */
  initializeWithService(service: DivineServiceState): void {
    this.state$.next(service);
  }

  /**
   * Limpia el servicio actual y vuelve a estado inicial
   */
  clearService(): void {
    this.resetState();
  }

  /**
   * Resetea la visibilidad de campos opcionales a su estado inicial (todos ocultos).
   * Llamar antes de aplicar la visibilidad de un servicio cargado para evitar
   * que valores de una sesión anterior persistan.
   */
  resetOptionalFieldsVisibility(): void {
    this.optionalFieldsVisibility$.next({ ...INITIAL_OPTIONAL_FIELDS_VISIBILITY });
  }

  /**
   * Muestra todos los campos opcionales que tienen valores
   */
  showFieldsWithValues(state: DivineServiceState): void {
    const visibility: Partial<OptionalFieldsVisibility> = {};

    if (state.memberInCharge) visibility.memberInCharge = true;
    if (state.location) visibility.location = true;
    if (state.notes) visibility.notes = true;
    if (state.offeringsARS > 0 || state.offeringsUSD > 0) visibility.offerings = true;
    if (state.visitsAdults > 0 || state.visitsMinors > 0) visibility.visits = true;
    if (state.newAdults > 0 || state.newMinors > 0) visibility.newSouls = true;

    this.updateFieldsVisibility(visibility);
  }
}
